import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import getUsageTracker from 'pricing-pages-lib/utils/getUsageTracker';
import { createProductConfig } from 'pricing-pages-lib/utils/productConfigUtils';
import { useState } from 'react';
import { PROFESSIONAL } from 'self-service-api/constants/ProductLevels';
import { USER } from 'self-service-api/constants/UnitTypes';
import CoreSeatsExplainerModal from 'ui-addon-upgrades/_core/common/components/seatsModals/CoreSeatsExplainerModal';
import Small from 'UIComponents/elements/Small';
import UIFlex from 'UIComponents/layout/UIFlex';
import UILink from 'UIComponents/link/UILink';
import { INTERSTITIAL } from '../../constants/tracking/Locations';
import { useAppSelector } from '../../reducers';
import { activeCurrencySelector } from '../../selectors/CurrencySelectors';
import { calculatedPriceSelector, calculatedPriceForProductConfigurationsInListSelector, planAdditionalCostsForProductConfigurationInListSelector } from '../../selectors/PricingSelectors';
import { productLevelForCoreSeatsSelector } from '../../selectors/ProductConfigsSelectors';
import { coreSeatSkuForProductLevelSelector, productForSkuIdSelector } from '../../selectors/ProductsSelectors';
import PriceDisplay from '../common/pricing/PriceDisplay';
import { InterstitialTileTitle } from '../interstitial/SharedComponents';
import { useCoreSeats } from '../interstitial/useCoreSeats';
import { StyledStepperInput, StyledTitleContainer } from './SharedComponents';
import { PlanTitle } from './ShoppingCart/ShoppingCartItem';
import styled from 'styled-components';
export const StyledStepperWrapper = styled.div.withConfig({
  displayName: "CoreSeatsPicker__StyledStepperWrapper",
  componentId: "sc-12x8ppa-0"
})(["max-width:100px;margin-right:8px;margin-left:8px;"]);

const CoreSeatsPicker = ({
  productConfigurations,
  setProductConfigurations,
  queryParamTerm,
  description,
  isShoppingCartItem = false,
  showStepper = true,
  trackingSource
}) => {
  const [coreSeatsExplainerVisible, setCoreSeatsExplainerVisible] = useState(false);
  const [showInitialSeatQuantity, setShowInitialSeatQuantity] = useState(true);
  const productLevelForCoreSeats = useAppSelector(state => productLevelForCoreSeatsSelector(state, {
    productConfigurations
  }));
  const {
    skuId: skuIdCoreSeat
  } = useAppSelector(state => coreSeatSkuForProductLevelSelector(state, {
    productLevel: productLevelForCoreSeats
  }));
  const {
    ownedCoreSeatsQuantity,
    coreSeatsToPurchaseQuantity,
    setCoreSeatsToPurchaseQuantity,
    includedCoreSeatsQuantity,
    initialCoreSeatsQuantity,
    updatedForUsers,
    updatedForOwnership
  } = useCoreSeats({
    productConfigurations,
    setProductConfigurations
  });
  const showingInitialQuantity = showInitialSeatQuantity && coreSeatsToPurchaseQuantity === initialCoreSeatsQuantity && trackingSource === INTERSTITIAL;
  const {
    apiName
  } = useAppSelector(state => productForSkuIdSelector(state, {
    skuId: skuIdCoreSeat
  }));

  const handleCoreSeatsChange = ({
    target: {
      value
    }
  }) => {
    const roundedValue = Math.round(parseFloat(value)) - includedCoreSeatsQuantity;
    const nextAdditionalUnitQuantity = roundedValue < ownedCoreSeatsQuantity ? ownedCoreSeatsQuantity : roundedValue;
    setCoreSeatsToPurchaseQuantity(nextAdditionalUnitQuantity);
    setShowInitialSeatQuantity(false);
    getUsageTracker().track('additionalUnitsPicker', {
      trackingSource,
      unitType: USER,
      productName: apiName,
      skuId: skuIdCoreSeat,
      ownedUnitQuantity: ownedCoreSeatsQuantity,
      displayedUnitQuantity: nextAdditionalUnitQuantity
    });
  };

  const currencyCode = useAppSelector(activeCurrencySelector);
  const additionalSeatsPrice = useAppSelector(state => calculatedPriceForProductConfigurationsInListSelector(state, {
    allProductConfigurations: productConfigurations,
    productConfigurations: [createProductConfig({
      skuId: skuIdCoreSeat
    })],
    queryParamTerm
  }).monthly.calculatedPrice);
  const price = useAppSelector(state => planAdditionalCostsForProductConfigurationInListSelector(state, {
    allProductConfigurations: productConfigurations,
    productConfigurations: productConfigurations.filter(config => config.skuId === skuIdCoreSeat),
    queryParamTerm
  }));
  const coreSeatProPrice = useAppSelector(state => {
    const {
      skuId: coreSeatSkuId
    } = coreSeatSkuForProductLevelSelector(state, {
      productLevel: PROFESSIONAL
    });
    return calculatedPriceSelector(state, {
      productConfigurations: [createProductConfig({
        skuId: coreSeatSkuId
      })]
    });
  });

  const handleLinkClick = () => {
    setCoreSeatsExplainerVisible(true);
    getUsageTracker().track('clickToViewModal', {
      name: 'core-seats-explainer-modal',
      location: INTERSTITIAL
    });
  };

  const TotalCoreSeatsText = () => /*#__PURE__*/_jsx(FormattedJSXMessage, {
    message: `interstitial.coreSeats.add.${productLevelForCoreSeats}_jsx`,
    elements: {
      Link: ({
        children
      }) => /*#__PURE__*/_jsx(UILink, {
        onClick: handleLinkClick,
        children: children
      })
    }
  });

  const AdditionalCoreSeatsText = () => /*#__PURE__*/_jsx(FormattedJSXMessage, {
    message: `interstitial.coreSeats.additional.${productLevelForCoreSeats}_jsx`,
    elements: {
      Link: ({
        children
      }) => /*#__PURE__*/_jsx(UILink, {
        onClick: handleLinkClick,
        children: children
      })
    }
  });

  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [coreSeatsExplainerVisible && /*#__PURE__*/_jsx(CoreSeatsExplainerModal, {
      handleModalClose: () => setCoreSeatsExplainerVisible(false),
      currencyCode: currencyCode,
      coreSeatProPrice: coreSeatProPrice.monthly.calculatedPrice
    }), /*#__PURE__*/_jsxs(UIFlex, {
      direction: "row",
      justify: "between",
      children: [/*#__PURE__*/_jsxs(UIFlex, {
        direction: "column",
        children: [/*#__PURE__*/_jsxs(StyledTitleContainer, {
          children: [isShoppingCartItem && /*#__PURE__*/_jsx(PlanTitle, {
            children: showStepper ? /*#__PURE__*/_jsx(TotalCoreSeatsText, {}) : /*#__PURE__*/_jsx(AdditionalCoreSeatsText, {})
          }), !isShoppingCartItem && /*#__PURE__*/_jsx(InterstitialTileTitle, {
            style: {
              marginBottom: 0
            },
            children: /*#__PURE__*/_jsx(TotalCoreSeatsText, {})
          })]
        }), /*#__PURE__*/_jsx("div", {
          className: "m-bottom-4",
          children: /*#__PURE__*/_jsxs(UIFlex, {
            direction: "column",
            children: [description && /*#__PURE__*/_jsx("span", {
              style: {
                maxWidth: '55ch'
              },
              children: description
            }), /*#__PURE__*/_jsx(FormattedReactMessage, {
              message: "planBuilder.priceDisplay.perMonthPerSeat",
              options: {
                price: /*#__PURE__*/_jsx(PriceDisplay, {
                  price: additionalSeatsPrice,
                  currencyCode: currencyCode
                })
              }
            })]
          })
        })]
      }), /*#__PURE__*/_jsxs(UIFlex, {
        className: "align-end",
        direction: "column",
        style: {
          maxWidth: '100px'
        },
        children: [showStepper && /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(StyledStepperInput, {
            value: coreSeatsToPurchaseQuantity + includedCoreSeatsQuantity,
            min: ownedCoreSeatsQuantity + includedCoreSeatsQuantity,
            onChange: handleCoreSeatsChange,
            "data-selenium-id": "core-seats-picker-input",
            disabledMinButtonTooltip: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "interstitial.seatsPicker.disabledMinButtonTooltip"
            }),
            disabledMinButtonTooltipProps: {
              placement: 'bottom'
            }
          }), showingInitialQuantity && (updatedForUsers || updatedForOwnership) && /*#__PURE__*/_jsx(Small, {
            use: "help",
            style: {
              marginLeft: '8px'
            },
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: updatedForUsers ? 'interstitial.seatsPicker.currentUsersTip' : 'interstitial.seatsPicker.currentCoreSeatsTip'
            })
          })]
        }), isShoppingCartItem && /*#__PURE__*/_jsxs("span", {
          className: "m-x-2",
          children: [/*#__PURE__*/_jsx(PriceDisplay, {
            className: "text ",
            price: price.monthly.calculatedPrice,
            currencyCode: currencyCode
          }), /*#__PURE__*/_jsx(FormattedMessage, {
            message: "planBuilder.priceDisplay.perMonth"
          })]
        })]
      })]
    })]
  });
};

export default CoreSeatsPicker;